// Working out which rows of the sheet are course rows, and which department and category
// each sits under.
//
// A department row ("Dept- Health and Social Care Courses") opens a section that runs
// until the next one. Departments repeat the same category headings - every department
// teaches the Foundation Year - so a category is only identified by its department and
// heading together. Rows above the first department row belong to no department.
//
// Both Check Doc and Fix Format consume the SAME walk. If they each derived their own,
// Fix Format could rewrite a row Check Doc never examined, or the duplicate-group rule
// could bucket courses under a heading the fixer disagreed about.
//
// Deliberately NOT excelStructure.isSectionRow. That helper additionally requires a
// section keyword or a completely empty D..L, so it misses short headings like "DET"
// and headings that carry a note in D. Missing a heading silently merges two categories
// into one, which manufactures duplicate-group anomalies that are not real. The two
// rules serve different jobs and must stay separate.

import { cellText, fixText } from "./courseNameRules";

const COLUMN_A = 0;
const COLUMN_B = 1;
const COLUMN_C = 2;
const COLUMN_E = 4;

/**
 * A category heading groups the courses beneath it, e.g. "February 2026 MSC - Sem 3".
 * Headings are usually merged across A:B or A:C, but merge metadata is unreliable and
 * the user may not have merged them at all, so this reads the text instead.
 */
export const isCategoryHeadingRow = (row) => {
  const a = cellText(row && row[COLUMN_A]);
  const b = cellText(row && row[COLUMN_B]);
  const c = cellText(row && row[COLUMN_C]);

  const seed = a || b;
  if (!seed) return false;
  if (/^\d+$/.test(seed)) return false; // a row number, not a heading
  if (!/[A-Za-z]/.test(seed)) return false; // stray numbers or symbols
  if (b.indexOf("~") !== -1 || c.indexOf("~") !== -1) return false; // a course row
  if (c) return false;

  return true;
};

// "Dept- Health and Social Care Courses" -> "Health and Social Care". The trailing
// "Courses" is dropped because some department rows carry it and some do not.
const DEPARTMENT_PATTERN = /^dept\s*[-–:]\s*(.*?)(?:\s+courses)?\s*$/i;

/** The department a heading names, or null when the heading is not a department row. */
const departmentOf = (heading) => {
  const match = DEPARTMENT_PATTERN.exec(heading);
  return match && match[1] ? match[1] : null;
};

/**
 * Which cell holds the course name for this row, if any.
 * Order matters: an empty C vetoes the row outright, even when B holds a tilde name.
 */
export const resolveCourseCell = (row) => {
  const b = cellText(row && row[COLUMN_B]);
  const c = cellText(row && row[COLUMN_C]);

  if (!c) return null;
  if (b.indexOf("~") !== -1) return { column: "B", index: COLUMN_B };
  if (c.indexOf("~") !== -1) return { column: "C", index: COLUMN_C };
  return null;
};

/**
 * Walk the sheet once and return every course row with its department and category.
 *
 * @param {Array<Array>} rows - sheet as an array of row arrays, index 0 = Excel row 1
 * @param {number} startRow - 1-based Excel row where data begins (from Settings)
 * @returns {{courseRows: Array, headingCount: number, scanned: number}}
 *          each course row carries `department` (string, or null above the first
 *          department row) and `categoryLabel`
 */
export const collectCourseRows = (rows, startRow) => {
  const courseRows = [];
  let department = null;
  let category = null;
  let headingCount = 0;
  let scanned = 0;

  const first = Math.max(1, Number(startRow) || 1);

  // The first department row can sit above the data start - the real workbook has
  // "Dept- Business" on row 3 with data from row 4. Look there for department rows only:
  // the title and the column headers are up there too and are not category headings.
  for (let i = 0; i < first - 1 && i < rows.length; i++) {
    const row = rows[i] || [];
    const named = departmentOf(cellText(row[COLUMN_A]) || cellText(row[COLUMN_B]));
    if (named) department = named;
  }

  for (let i = first - 1; i < rows.length; i++) {
    const row = rows[i] || [];
    const rowNumber = i + 1;
    scanned++;

    if (isCategoryHeadingRow(row)) {
      const heading = cellText(row[COLUMN_A]) || cellText(row[COLUMN_B]);
      const named = departmentOf(heading);
      if (named) {
        // The previous department's last category must not leak into this one.
        department = named;
        category = null;
      } else {
        category = heading;
        headingCount++;
      }
      continue;
    }

    const cell = resolveCourseCell(row);
    if (!cell) continue;

    const b = cellText(row[COLUMN_B]);
    const c = cellText(row[COLUMN_C]);

    courseRows.push({
      rowNumber,
      column: cell.column,
      ref: cell.column + rowNumber,
      // raw keeps the spacing that segment 4 sometimes carries; text is for comparison.
      raw: fixText(row[cell.index]),
      text: cellText(row[cell.index]),
      // The tutor, compared with segment 7 by Check Doc. Fix Format does not read it.
      tutor: cellText(row[COLUMN_E]),
      department,
      category,
      // Rows above the first heading are kept, not dropped: duplicates at the top of the
      // sheet are still duplicates.
      categoryLabel: category || "rows above the first category heading",
      // Only the B copy is read and fixed, so a stale C would silently disagree with it.
      bothColumns: b.indexOf("~") !== -1 && c.indexOf("~") !== -1,
    });
  }

  return { courseRows, headingCount, scanned };
};
